import { formatAmount, withCurrency, type TokenScale } from "@/lib/format/amount";

/* A player's reward history, read the way the whitelabel reads it: from
   `/rewards/transactions?accountId=…`, the feed behind the Live Rewards table,
   rather than from the `/rewards/history` claim ledger.

   What the feed says about a row's type is split over two fields, and that is
   what the account page got wrong. `type` is only the *kind* of claim — the
   platform writes `${incentiveKind}-claim` — so a cashback, a bounty and a
   free-spin payout all arrive as "incentive-claim". Which reward it was is in
   `name`, where the platform puts the bonus type with its underscores turned
   into spaces: "Cashback", "Rakeback", "Free spin", "Free money", "Bounty".
   Reading only `type` printed every one of them as "incentive-claim".

   A custom reward is the other way round: the platform fills `name` with
   coalesce(reward type, custom reward name), so there it is whatever the
   operator called the reward, and the row is told apart by its `type`. Those
   are left out of the account's history altogether. */

export type ApiRewardTransaction = {
  id: string;
  type: string;
  name: string | null;
  amount: string;
  hash: string | null;
  createdAt: string;
  status?: string;
  token: TokenScale & {
    symbol: string;
    network?: { environment: string; attributes?: { chainId?: number } | Record<string, unknown> };
  };
};

/* The platform's incentive kind for a custom reward is "reward", so the feed
   says "reward-claim" (the whitelabel labels it Race); the claim ledger's
   spelling is kept beside it in case a row ever carries that instead. */
const CUSTOM_REWARD_TYPES = new Set(["reward-claim", "custom-reward-claim"]);

export const isCustomReward = (transaction: Pick<ApiRewardTransaction, "type">) =>
  CUSTOM_REWARD_TYPES.has(transaction.type);

/* The names the site uses for the platform's bonus types, keyed by the type
   with case and separators removed so "Free spin", "free_spin" and "Freespin"
   all land on one label. */
const TYPE_LABEL: Record<string, string> = {
  cashback: "Cashback",
  rakeback: "Rakeback",
  freespin: "Freespin",
  freespins: "Freespin",
  freemoney: "Free Money",
  bounty: "Bounty",
  tierassignment: "Tier Reward",
};

const squash = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");

export const rewardTypeLabel = ({ type, name }: Pick<ApiRewardTransaction, "type" | "name">): string => {
  const fromName = name ? TYPE_LABEL[squash(name)] : undefined;

  if (fromName) {
    return fromName;
  }

  /* The older rakeback claim carries no name; its kind is its type. */
  const fromKind = TYPE_LABEL[squash(type.replace(/-claim$/, ""))];

  return fromKind ?? name ?? "Reward";
};

/* The order the kit's filter pills run in; a label it does not know goes last. */
const LABEL_ORDER = ["Rakeback", "Free Money", "Cashback", "Bounty", "Freespin"];

export type RewardHistoryRow = {
  id: string;
  label: string;
  amount: string;
  createdAt: string;
  hash: string;
  fullHash: string | undefined;
  chainId: number | undefined;
};

export const toRewardHistory = (transactions: ApiRewardTransaction[]): RewardHistoryRow[] =>
  transactions
    .filter((transaction) => !isCustomReward(transaction))
    .map((transaction) => ({
      id: transaction.id,
      label: rewardTypeLabel(transaction),
      amount: `+${withCurrency(formatAmount(transaction.amount, transaction.token), transaction.token.symbol)}`,
      createdAt: transaction.createdAt,
      hash: transaction.hash ? `${transaction.hash.slice(0, 6)}…${transaction.hash.slice(-4)}` : "—",
      fullHash: transaction.hash ?? undefined,
      chainId:
        transaction.token.network?.environment === "evm"
          ? (transaction.token.network.attributes as { chainId?: number } | undefined)?.chainId
          : undefined,
    }));

export const rewardLabels = (rows: RewardHistoryRow[]): string[] =>
  [...new Set(rows.map((row) => row.label))].sort((left, right) => {
    const a = LABEL_ORDER.indexOf(left);
    const b = LABEL_ORDER.indexOf(right);

    return (a < 0 ? LABEL_ORDER.length : a) - (b < 0 ? LABEL_ORDER.length : b) || left.localeCompare(right);
  });
